import { acceptHMRUpdate, defineStore } from 'pinia'
import { axiosInstance } from '@/api/api'

export type WorkspaceRole = 'admin' | 'member'

export type WorkspaceType = 'personal' | 'company'

export interface WorkspacePermissions {
  can_access_invoices: boolean
  can_access_company_details: boolean
  can_access_loyalty: boolean
}

export interface ActiveWorkspace {
  id: number
  name: string
  workspace_type?: WorkspaceType
  is_verified: boolean
  has_pending_verification?: boolean
  company_id?: number
  company_name?: string
  vat_number?: string
  street?: string
  street_number?: string
  city?: string
  postcode?: string
  country?: string
  current_user_role?: WorkspaceRole | null
  current_user_permissions?: WorkspacePermissions | null
}

export interface WorkspaceMember {
  id: number
  user: number
  user_email: string
  user_name: string
  user_phone?: string
  position?: string
  role: WorkspaceRole
  is_active_workspace: boolean
  can_access_invoices: boolean
  can_access_company_details: boolean
  can_access_loyalty: boolean
  created_at: string
}

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired'

export interface WorkspaceInvitationRow {
  id: number
  workspace: number
  email: string
  name: string
  phone: string
  position?: string
  invited_by_email: string | null
  status: InvitationStatus
  can_access_invoices: boolean
  can_access_company_details: boolean
  can_access_loyalty: boolean
  expires_at: string
  accepted_at: string | null
  created_at: string
}

export interface InviteMemberPayload {
  email: string
  name: string
  surname: string
  phone: string
  position: string
  can_access_invoices?: boolean
  can_access_company_details?: boolean
  can_access_loyalty?: boolean
}

export interface InviteMemberResult {
  silent_add?: boolean
  membership?: WorkspaceMember

  // if not silent_add, the response is a WorkspaceInvitationRow payload
  invitation?: WorkspaceInvitationRow
}

export interface PublicInvitation {
  email: string
  name: string
  workspace_name: string
  invited_by_name: string
  status: InvitationStatus
  expires_at: string
  requires_password: boolean
}

export type AdminAccessRequestStatus = 'pending' | 'approved' | 'rejected'

export interface AdminAccessRequest {
  id: number
  workspace: number
  status: AdminAccessRequestStatus
  resolved_at: string | null
  resolved_note: string
  created_at: string
}

export interface WorkspaceOrder {
  id: number
  order_number: string
  status: string
  created: string
  distance: number | null
  price: string | null
  pickup_city: string
  pickup_country: string
  pickup_postcode: string
  pickup_datetime: string | null
  delivery_city: string
  delivery_country: string
  delivery_postcode: string
  delivery_datetime: string | null
  tail_lift_required: boolean
  frigo_required: boolean
  sideboards_required: boolean
  adr_required: boolean
  hash: string | null
  manager_email: string | null
  manager_name: string | null
}

export interface WorkspaceInvoice {
  id: number
  invoice_number: string
  order_numbers: string[]
  issue_date: string | null
  due_date: string | null
  tax_point_date: string | null
  amount: string | null
  vat_amount: string | null
  currency: string
  status: string
  is_overdue: boolean
  invoice_type: string
}

export interface OrderFilters {
  order_number?: string
  status?: string
  created_after?: string
  created_before?: string
  price_min?: string
  price_max?: string
  pickup_city?: string
  delivery_city?: string
  pickup_country?: string
  delivery_country?: string
  pickup_postcode?: string
  delivery_postcode?: string
  tail_lift?: string
  frigo?: string
  sideboards?: string
  adr?: string
}

export interface InvoiceFilters {
  invoice_number?: string
  status?: string
  order_number?: string
  issue_date_after?: string
  issue_date_before?: string
  due_date_after?: string
  due_date_before?: string
  amount_min?: string
  amount_max?: string
}

interface PaginatedResponse<T> {
  results: T[]
  count: number
  page: number
  page_size: number
}

export interface LoyaltyGadget {
  id: number
  title: string
  description: string
  points: number
  stock: number
  multiple_variants: boolean
  variant_options: string[]
  variant_stock: Record<string, number>
  image: string | null
}

export interface LoyaltyExpiry {
  points: number
  expires_at: string
}

export interface LoyaltyBalance {
  available_points: number
  min_points_to_order: number
  min_points_to_unlock?: number
  can_order: boolean
  points_validity_months: number
  points_per_turnover: { points: number; turnover: string; currency: string }
  upcoming_expiry: LoyaltyExpiry[]
}

export interface LoyaltyOrderItemPayload {
  gadget_id: number
  variant: string
  quantity: number
}

export interface LoyaltyOrderPayload {
  items: LoyaltyOrderItemPayload[]
  address_name: string
  street: string
  postcode: string
  city: string
  country: string
  full_name: string
  email: string
  phone: string
  note: string
}

export interface LoyaltyOrderItem {
  title: string
  variant: string
  quantity: number
  points: number
  subtotal: number
}

/**
 * Swida owns the status list and may extend it before the SPA is updated,
 * so `status` stays a plain string; the values known today are
 * `KnownLoyaltyOrderStatus` in `@/utils/loyalty-order-status`.
 */
export interface LoyaltyOrder {
  id: number
  identifier: string
  status: string
  total: number
  created: string
  date_shipped: string | null
  items: LoyaltyOrderItem[]
}

export interface WorkspaceListItem {
  id: number
  name: string
  company_name: string
  vat_number: string
  is_verified: boolean
  has_pending_verification?: boolean
  company_id: number | null
  current_user_role: WorkspaceRole | null
  current_user_is_creator?: boolean
  current_user_can_manage?: boolean
  workspace_type: 'personal' | 'company'
  member_count: number
}

export type JoinRequestStatus = 'pending' | 'approved' | 'rejected'
export type VerificationRequestStatus = 'pending' | 'approved' | 'rejected'

export interface WorkspaceVerificationRequestRow {
  id: number
  workspace: number
  requested_by: number
  status: VerificationRequestStatus
  resolved_at: string | null
  note: string
  swida_request_id: number | null
  created_at: string
}

export interface VerificationOrJoinResult {

  // Join-existing — backend created a join request to an existing verified
  // workspace (and auto-deleted the draft). Use the same toast wording as
  // Add new company.
  join_request?: boolean
  join_request_id?: number
  workspace_name?: string

  // Verify-via-Swida — backend created a local WorkspaceVerificationRequest
  // and pushed it to Swida. The frontend should show "pending" UI on the
  // workspace.
  id?: number
  status?: VerificationRequestStatus
}

export interface WorkspaceJoinRequestRow {
  id: number
  workspace: number
  workspace_name: string
  requested_by: number
  requested_by_email: string
  requested_by_name: string
  requested_by_phone: string
  status: JoinRequestStatus
  resolved_by: number | null
  resolved_by_email: string | null
  resolved_at: string | null
  note: string
  created_at: string
}

export interface CreateWorkspaceResult {

  // When the VAT matches an already-verified workspace, the backend sends
  // a join request to that workspace's admin(s) instead of creating a
  // duplicate. The caller gets a 202 with these fields.
  join_request?: boolean
  join_request_id?: number
  workspace_name?: string

  // Otherwise the response is an ActiveWorkspace payload.
  id?: number
  name?: string
  is_verified?: boolean
}

// The rows of `first`, then those of `rest` with an id not among them.
function mergeById<T extends { id: number }>(first: T[], rest: T[]): T[] {
  const ids = new Set(first.map(row => row.id))

  return [...first, ...rest.filter(row => !ids.has(row.id))]
}

export interface WorkspaceEmailPreference {
  key: string
  name: string
  description: string
  is_enabled: boolean
}

export interface NotificationRecipient {
  name: string
  email: string
}

/**
 * The caller's own transport notification settings for one workspace: the
 * workspace-scoped e-mail preference groups and the additional recipients.
 * Account-wide groups such as Magic link stay on /api/auth/user/.
 */
export interface WorkspaceNotificationSettings {
  workspace: number
  email_preferences: WorkspaceEmailPreference[]
  notification_recipients: NotificationRecipient[]
}

export interface WorkspaceNotificationSettingsPayload {
  email_preferences: { key: string; is_enabled: boolean }[]
  notification_recipients: NotificationRecipient[]
}

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    // Bumped by clearSession() on sign-out and sign-in.
    session: 0,

    activeWorkspace: null as ActiveWorkspace | null,
    workspaces: [] as WorkspaceListItem[],
    workspacesLoading: false,

    orders: [] as WorkspaceOrder[],
    ordersCount: 0,
    ordersPage: 1,
    ordersPageSize: 10,
    ordersLoading: false,
    ordersError: null as string | null,

    invoices: [] as WorkspaceInvoice[],
    invoicesCount: 0,
    invoicesPage: 1,
    invoicesPageSize: 10,
    invoicesLoading: false,
    invoicesError: null as string | null,

    gadgets: [] as LoyaltyGadget[],
    gadgetsLoading: false,
    gadgetsError: null as string | null,
    loyaltyBalance: null as LoyaltyBalance | null,
    loyaltyBalanceLoading: false,
    loyaltyBalanceError: null as string | null,
    loyaltyOrderSubmitting: false,
    loyaltyOrders: [] as LoyaltyOrder[],
    loyaltyOrdersLoading: false,
    loyaltyOrdersError: null as string | null,

    adminAccessRequest: null as AdminAccessRequest | null,
    adminAccessRequestLoading: false,
    adminAccessRequestError: null as string | null,

    members: [] as WorkspaceMember[],
    pendingInvitations: [] as WorkspaceInvitationRow[],
    pendingJoinRequests: [] as WorkspaceJoinRequestRow[],
    myJoinRequests: [] as WorkspaceJoinRequestRow[],
    accessLoading: false,
    accessError: null as string | null,

    verificationRequest: null as WorkspaceVerificationRequestRow | null,
    verificationLoading: false,
  }),

  actions: {
    /**
     * Forgets everything loaded for the signed-in user (workspaces, orders,
     * invoices, loyalty, members...), on sign-out and on sign-in, so the next
     * user of the tab never sees it. Answers to the workspace requests sent
     * before are dropped, so a slow one cannot bring the previous user's
     * workspaces back.
     */
    clearSession() {
      const session = this.session + 1

      this.$reset()
      this.session = session
    },

    async fetchActiveWorkspace() {
      const session = this.session

      try {
        const response = await axiosInstance.get<ActiveWorkspace>('/api/workspaces/active/')

        if (session === this.session)
          this.activeWorkspace = response.data
      }
      catch {
        if (session === this.session)
          this.activeWorkspace = null
      }
    },

    async fetchWorkspaces() {
      const session = this.session

      this.workspacesLoading = true
      try {
        const response = await axiosInstance.get<WorkspaceListItem[]>('/api/workspaces/')

        if (session === this.session)
          this.workspaces = response.data
      }
      catch {
        if (session === this.session)
          this.workspaces = []
      }
      finally {
        if (session === this.session)
          this.workspacesLoading = false
      }
    },

    async switchWorkspace(workspaceId: number) {
      const session = this.session

      const response = await axiosInstance.post<ActiveWorkspace>(
        '/api/workspaces/switch/',
        { workspace_id: workspaceId },
      )

      if (session === this.session)
        this.activeWorkspace = response.data

      return response.data
    },

    async createCompanyWorkspace(payload: {
      company_name?: string
      vat_number?: string
      street?: string
      street_number?: string
      city?: string
      postcode?: string
      country?: string
      is_vat_payer: boolean
    }): Promise<CreateWorkspaceResult> {
      const response = await axiosInstance.post<CreateWorkspaceResult>(
        '/api/workspaces/',
        payload,
      )

      // 202 = join request was sent, 201 = new workspace created.
      if (response.data?.join_request)
        await this.fetchMyJoinRequests()
      else
        await this.fetchWorkspaces()

      return response.data
    },

    async fetchMyJoinRequests() {
      const session = this.session

      try {
        const response = await axiosInstance.get<WorkspaceJoinRequestRow[]>(
          '/api/workspaces/my-join-requests/',
        )

        if (session === this.session)
          this.myJoinRequests = response.data
      }
      catch {
        if (session === this.session)
          this.myJoinRequests = []
      }
    },

    async fetchWorkspaceJoinRequests(workspaceId: number) {
      try {
        const response = await axiosInstance.get<WorkspaceJoinRequestRow[]>(
          `/api/workspaces/${workspaceId}/join-requests/`,
        )

        // Admin page needs only pending — history is not in scope here.
        this.pendingJoinRequests = response.data.filter(r => r.status === 'pending')
      }
      catch {
        this.pendingJoinRequests = []
      }
    },

    async fetchVerificationRequest(workspaceId: number) {
      this.verificationLoading = true
      try {
        const response = await axiosInstance.get<WorkspaceVerificationRequestRow | { status: null }>(
          `/api/workspaces/${workspaceId}/verification-request/`,
        )

        this.verificationRequest
          = response.data && (response.data as WorkspaceVerificationRequestRow).id
            ? (response.data as WorkspaceVerificationRequestRow)
            : null
      }
      catch {
        this.verificationRequest = null
      }
      finally {
        this.verificationLoading = false
      }
    },

    async requestVerification(
      workspaceId: number,
      payload: { vat_number?: string; company_name?: string },
    ): Promise<VerificationOrJoinResult> {
      const response = await axiosInstance.post<VerificationOrJoinResult>(
        `/api/workspaces/${workspaceId}/request-verification/`,
        payload,
      )

      // Join-existing path — draft was auto-deleted, refresh user-side state.
      if (response.data?.join_request) {
        await Promise.all([
          this.fetchActiveWorkspace(),
          this.fetchWorkspaces(),
          this.fetchMyJoinRequests(),
        ])
      }
      else {
        // Verify-via-Swida path — verification record created. Refresh the
        // workspace list too so ``has_pending_verification`` flips on the
        // sidebar row and the bin icon hides without a page reload.
        await Promise.all([
          this.fetchVerificationRequest(workspaceId),
          this.fetchWorkspaces(),
        ])
      }

      return response.data
    },

    async resolveJoinRequest(requestId: number, approved: boolean) {
      await axiosInstance.post(
        `/api/workspaces/join-requests/${requestId}/resolve/`,
        { approved },
      )

      // Drop the resolved request from the pending list; if approved, also
      // refresh members so the new membership shows up.
      this.pendingJoinRequests = this.pendingJoinRequests.filter(
        r => r.id !== requestId,
      )
    },

    async deleteWorkspace(workspaceId: number) {
      await axiosInstance.delete(`/api/workspaces/${workspaceId}/`)

      // The server may have flipped our active workspace to the personal
      // fallback if we deleted the one we were on — refetch both so the UI
      // mirrors the new server state.
      await Promise.all([this.fetchActiveWorkspace(), this.fetchWorkspaces()])
    },

    async fetchOrders(filters: OrderFilters = {}, page?: number, pageSize?: number) {
      this.ordersLoading = true
      this.ordersError = null

      if (page !== undefined)
        this.ordersPage = page
      if (pageSize !== undefined)
        this.ordersPageSize = pageSize

      try {
        const params: Record<string, string | number> = {
          page: this.ordersPage,
          page_size: this.ordersPageSize,
        }

        // Add non-empty filters
        for (const [key, value] of Object.entries(filters)) {
          if (value)
            params[key] = value
        }

        const response = await axiosInstance.get<PaginatedResponse<WorkspaceOrder>>(
          '/api/workspaces/orders/',
          { params },
        )

        this.orders = response.data.results
        this.ordersCount = response.data.count
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.ordersError = axiosErr?.response?.data?.detail || 'Failed to fetch orders'
        this.orders = []
        this.ordersCount = 0
      }
      finally {
        this.ordersLoading = false
      }
    },

    async fetchAdminAccessRequest(workspaceId: number) {
      this.adminAccessRequestLoading = true
      this.adminAccessRequestError = null
      try {
        const response = await axiosInstance.get<AdminAccessRequest | { status: null }>(
          `/api/workspaces/${workspaceId}/admin-access-request/`,
        )

        this.adminAccessRequest = response.data && (response.data as AdminAccessRequest).id
          ? (response.data as AdminAccessRequest)
          : null
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.adminAccessRequestError = axiosErr?.response?.data?.detail || 'Failed to fetch admin access request'
        this.adminAccessRequest = null
      }
      finally {
        this.adminAccessRequestLoading = false
      }
    },

    async createAdminAccessRequest(workspaceId: number) {
      this.adminAccessRequestLoading = true
      this.adminAccessRequestError = null
      try {
        const response = await axiosInstance.post<AdminAccessRequest>(
          `/api/workspaces/${workspaceId}/admin-access-request/`,
        )

        this.adminAccessRequest = response.data

        return response.data
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { status?: number; data?: { detail?: string } } }

        this.adminAccessRequestError = axiosErr?.response?.data?.detail || 'Failed to create admin access request'
        throw error
      }
      finally {
        this.adminAccessRequestLoading = false
      }
    },

    async fetchNotificationSettings(workspaceId: number): Promise<WorkspaceNotificationSettings> {
      const response = await axiosInstance.get<WorkspaceNotificationSettings>(
        `/api/workspaces/${workspaceId}/notification-settings/`,
      )

      return response.data
    },

    async updateNotificationSettings(
      workspaceId: number,
      payload: WorkspaceNotificationSettingsPayload,
    ): Promise<WorkspaceNotificationSettings> {
      const response = await axiosInstance.put<WorkspaceNotificationSettings>(
        `/api/workspaces/${workspaceId}/notification-settings/`,
        payload,
      )

      return response.data
    },

    // ------- Access management (INQ-685) -------

    async fetchMembers(workspaceId: number) {
      this.accessLoading = true
      this.accessError = null

      // An invitation added while the list loads may be missing from it (INQ-820).
      const invitedBefore = new Set(this.pendingInvitations.map(i => i.id))
      try {
        const [membersRes, invitesRes] = await Promise.all([
          axiosInstance.get<WorkspaceMember[]>(
            `/api/workspaces/${workspaceId}/members/`,
          ),
          axiosInstance.get<WorkspaceInvitationRow[]>(
            `/api/workspaces/${workspaceId}/invitations/`,
          ),
        ])

        this.members = membersRes.data

        const invitedMeanwhile = this.pendingInvitations.filter(i => !invitedBefore.has(i.id))

        this.pendingInvitations = mergeById(invitedMeanwhile, invitesRes.data)
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.accessError = axiosErr?.response?.data?.detail || 'Failed to fetch workspace members'
      }
      finally {
        this.accessLoading = false
      }
    },

    async inviteMember(workspaceId: number, payload: InviteMemberPayload): Promise<InviteMemberResult> {
      const response = await axiosInstance.post(
        `/api/workspaces/${workspaceId}/invitations/`,
        payload,
      )

      const data = response.data
      if (data?.silent_add) {
        this.members = mergeById(this.members, [data.membership])

        return { silent_add: true, membership: data.membership }
      }
      const invitation = data as WorkspaceInvitationRow

      // The member list, loading meanwhile, may already hold it (INQ-820).
      this.pendingInvitations = mergeById([invitation], this.pendingInvitations)

      return { invitation }
    },

    async updateMemberPermissions(workspaceId: number, userId: number, perms: Partial<WorkspacePermissions>) {
      const response = await axiosInstance.patch<WorkspaceMember>(
        `/api/workspaces/${workspaceId}/members/${userId}/`,
        perms,
      )

      this.members = this.members.map(m => (m.user === userId ? response.data : m))

      return response.data
    },

    async updateMemberRole(workspaceId: number, userId: number, role: WorkspaceRole) {
      const response = await axiosInstance.patch<WorkspaceMember>(
        `/api/workspaces/${workspaceId}/members/${userId}/`,
        { role },
      )

      this.members = this.members.map(m => (m.user === userId ? response.data : m))

      return response.data
    },

    async removeMember(workspaceId: number, userId: number) {
      await axiosInstance.delete(`/api/workspaces/${workspaceId}/members/${userId}/`)
      this.members = this.members.filter(m => m.user !== userId)
    },

    async revokeInvitation(workspaceId: number, invitationId: number) {
      await axiosInstance.delete(
        `/api/workspaces/${workspaceId}/invitations/${invitationId}/`,
      )
      this.pendingInvitations = this.pendingInvitations.filter(i => i.id !== invitationId)
    },

    async getInvitation(token: string): Promise<PublicInvitation> {
      const response = await axiosInstance.get<PublicInvitation>(
        `/api/invitations/${token}/`,
      )

      return response.data
    },

    async acceptInvitation(token: string, password?: string) {
      // The JWT pair signs the recipient in (pages/invite/[token].vue).
      const response = await axiosInstance.post<{ access: string; refresh: string }>(
        `/api/invitations/${token}/accept/`,
        password ? { password } : {},
      )

      return response.data
    },

    async fetchInvoices(filters: InvoiceFilters = {}, page?: number, pageSize?: number) {
      this.invoicesLoading = true
      this.invoicesError = null

      if (page !== undefined)
        this.invoicesPage = page
      if (pageSize !== undefined)
        this.invoicesPageSize = pageSize

      try {
        const params: Record<string, string | number> = {
          page: this.invoicesPage,
          page_size: this.invoicesPageSize,
        }

        for (const [key, value] of Object.entries(filters)) {
          if (value)
            params[key] = value
        }

        const response = await axiosInstance.get<PaginatedResponse<WorkspaceInvoice>>(
          '/api/workspaces/invoices/',
          { params },
        )

        this.invoices = response.data.results
        this.invoicesCount = response.data.count
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.invoicesError = axiosErr?.response?.data?.detail || 'Failed to fetch invoices'
        this.invoices = []
        this.invoicesCount = 0
      }
      finally {
        this.invoicesLoading = false
      }
    },

    // --- Loyalty program (Swida company shop) ---

    async fetchGadgets() {
      this.gadgetsLoading = true
      this.gadgetsError = null
      try {
        const response = await axiosInstance.get<PaginatedResponse<LoyaltyGadget>>(
          '/api/workspaces/loyalty/gadgets/',
        )

        this.gadgets = response.data.results
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.gadgetsError = axiosErr?.response?.data?.detail || 'Failed to load the catalogue'
        this.gadgets = []
      }
      finally {
        this.gadgetsLoading = false
      }
    },

    async fetchLoyaltyBalance() {
      this.loyaltyBalanceLoading = true
      this.loyaltyBalanceError = null
      try {
        const response = await axiosInstance.get<LoyaltyBalance>(
          '/api/workspaces/loyalty/balance/',
        )

        this.loyaltyBalance = response.data
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.loyaltyBalanceError = axiosErr?.response?.data?.detail || 'Failed to load the points balance'
        this.loyaltyBalance = null
      }
      finally {
        this.loyaltyBalanceLoading = false
      }
    },

    async createLoyaltyOrder(payload: LoyaltyOrderPayload) {
      this.loyaltyOrderSubmitting = true
      try {
        const response = await axiosInstance.post('/api/workspaces/loyalty/orders/', payload)

        return response.data
      }
      finally {
        this.loyaltyOrderSubmitting = false
      }
    },

    async fetchLoyaltyOrders() {
      this.loyaltyOrdersLoading = true
      this.loyaltyOrdersError = null
      try {
        const response = await axiosInstance.get<PaginatedResponse<LoyaltyOrder>>(
          '/api/workspaces/loyalty/orders/',
        )

        this.loyaltyOrders = response.data.results
      }
      catch (error: unknown) {
        const axiosErr = error as { response?: { data?: { detail?: string } } }

        this.loyaltyOrdersError = axiosErr?.response?.data?.detail || 'Failed to load order history'
        this.loyaltyOrders = []
      }
      finally {
        this.loyaltyOrdersLoading = false
      }
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useWorkspaceStore, import.meta.hot))
